/**
 * Branch Wise Promotions Screen (6.1.11)
 * POS Module - Masters — PDF-exact fields
 * UOM Master Style + ReportGrid
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Save, RefreshCw, AlertCircle, Edit2, Trash2 } from 'lucide-react';
import ReportGrid from '../../../components/report-engine/ReportGrid';

const BranchWisePromotionsScreen = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const bgColor = isDark ? 'bg-[#1a1a2e]' : 'bg-gray-100';
  const cardBg = isDark ? 'bg-[#16213e]' : 'bg-white';
  const inputBg = isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50';
  const borderColor = isDark ? 'border-[#1f4068]' : 'border-gray-300';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const labelColor = isDark ? 'text-gray-300' : 'text-gray-700';
  const sectionBg = isDark ? 'bg-[#1f2937]' : 'bg-gray-50';

  const [branchName, setBranchName] = useState('');
  const [promotionId, setPromotionId] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      setData([
        { id: 1, promotionId: 'PROMO001', promoName: 'Summer Sale', promotionName: 'Summer Sale 2024', pType: 'Value', effFromDate: '2024-03-01', effToDate: '2024-03-31', active: true },
        { id: 2, promotionId: 'PROMO002', promoName: 'Buy 2 Get 1', promotionName: 'Buy 2 Get 1 Free', pType: 'Quantity', effFromDate: '2024-03-01', effToDate: '2024-04-15', active: true },
        { id: 3, promotionId: 'PROMO003', promoName: 'Festive', promotionName: 'Festive Discount', pType: 'Value', effFromDate: '2024-03-10', effToDate: '2024-03-20', active: false },
      ]);
    } catch { setError('Failed to load data'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = () => {
    if (!branchName) { setError('Branch Name is required'); return; }
    alert('Branch Wise Promotions saved!');
  };
  const handleSort = (field) => { if (sortColumn === field) setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc'); else { setSortColumn(field); setSortDirection('asc'); } };
  const sortedData = useMemo(() => { if (!sortColumn) return data; return [...data].sort((a, b) => { const av = a[sortColumn], bv = b[sortColumn]; const cmp = typeof av === 'number' ? av - bv : String(av || '').localeCompare(String(bv || '')); return sortDirection === 'asc' ? cmp : -cmp; }); }, [data, sortColumn, sortDirection]);
  const paginatedData = useMemo(() => sortedData.slice((page - 1) * pageSize, page * pageSize), [sortedData, page, pageSize]);
  const pagination = useMemo(() => ({ page, pageSize, total: data.length, totalPages: Math.ceil(data.length / pageSize) || 1 }), [data.length, page, pageSize]);

  const gridColumns = useMemo(() => [
    { field: 'promotionId', header: 'Promotion Id', width: 100 },
    { field: 'promoName', header: 'Promo Name', width: 130 },
    { field: 'promotionName', header: 'Promotion Name', width: 170 },
    { field: 'pType', header: 'PType', width: 80 },
    { field: 'effFromDate', header: 'Eff From Date', width: 100 },
    { field: 'effToDate', header: 'Eff To Date', width: 100 },
    { field: 'active', header: 'Active', width: 70, align: 'center', render: (val) => (<span className={`px-2 py-0.5 rounded-full text-xs ${val ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{val ? 'Yes' : 'No'}</span>) },
  ], []);

  return (
    <div className={`h-full flex flex-col ${bgColor} ${textColor} p-1 overflow-auto`} data-testid="branch-wise-promotions-screen">
      <div className={`${cardBg} rounded-lg p-3 mb-1 border ${borderColor} shrink-0`}>
        <div className="flex items-center justify-between">
          <div><h1 className="text-lg font-bold">Branch Wise Promotions</h1><p className="text-xs text-gray-400">Screen ID: 6.1.11 | POS Module</p></div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} className="p-2 rounded bg-blue-600 hover:bg-blue-700 text-white" data-testid="refresh-btn"><RefreshCw size={16} className={loading ? 'animate-spin' : ''} /></button>
            <button onClick={handleSave} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm" data-testid="save-btn"><Save size={16} /> Save</button>
          </div>
        </div>
      </div>
      {error && (<div className="mb-1 p-2 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2 shrink-0"><AlertCircle className="text-red-400" size={18} /><span className="text-red-400 text-sm">{error}</span><button onClick={() => setError(null)} className="ml-auto text-red-400">x</button></div>)}
      <div className={`${cardBg} rounded-lg border ${borderColor} p-2 shrink-0`}>
        <div className={`${sectionBg} rounded-lg p-2 border ${borderColor} max-w-lg`}>
          <h2 className={`text-sm font-semibold mb-2 pb-1 border-b ${borderColor}`}>Branch Promotion Setup</h2>
          <div className="flex items-center mb-1.5"><label className={`w-28 text-xs ${labelColor} after:content-["*"] after:text-red-500 after:ml-1`}>Branch Name</label><select value={branchName} onChange={(e) => setBranchName(e.target.value)} className={`flex-1 px-2 py-1.5 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`} data-testid="select-branchName"><option value="">--Select Branch--</option><option value="Main Branch">Main Branch</option><option value="Branch 2">Branch 2</option><option value="Branch 3">Branch 3</option></select></div>
          <div className="flex items-center mb-1.5"><label className={`w-28 text-xs ${labelColor}`}>Promotion Id</label><input value={promotionId} onChange={(e) => setPromotionId(e.target.value)} className={`flex-1 px-2 py-1.5 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`} data-testid="input-promotionId" /></div>
          <div className="flex items-center mb-1.5"><label className={`w-28 text-xs ${labelColor}`}>Select All</label><input type="checkbox" checked={selectAll} onChange={(e) => setSelectAll(e.target.checked)} className="w-4 h-4" /></div>
        </div>
      </div>
      <div className={`${cardBg} rounded-lg border ${borderColor} mt-1 flex flex-col overflow-hidden`} style={{ minHeight: 350 }}>
        <div className={`px-2 py-1 border-b ${borderColor} shrink-0`}><h3 className="text-sm font-semibold">Promotions List</h3></div>
        <div className="flex flex-col" style={{ height: 350 }}>
          <ReportGrid columns={gridColumns} data={paginatedData} theme={theme} loading={loading} showRowNumbers={true} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} pagination={pagination} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} emptyMessage="No data found" />
        </div>
      </div>
    </div>
  );
};

export default BranchWisePromotionsScreen;
